import { Injectable } from '@angular/core';
import { AwsService, HelperService, TranslateService } from '__services';
import * as AWS from 'aws-sdk';
import { Observable, Observer } from 'rxjs';
import { map, shareReplay, take } from 'rxjs/operators';
import { environment } from '~/environments';
import { HttpService } from './http.service';

@Injectable({
  providedIn: 'root',
})
export class CommonService {
  countries: Observable<any>;
  personas: Observable<any>;
  users: Observable<any>;
  locations: Observable<any>;
  credentialOfUser: any;
  companyIdentifier: any;
  downloadState: {[x: string]: boolean} = {} ;

  constructor(
    private http: HttpService,
    private translateService: TranslateService,
    private awsService: AwsService
  ) {
  }

  getCountries(): any {
    this.countries = this.http
      .get(`/countries`, { sort: 'name' })
      .pipe(shareReplay(1), take(1));
  }

  getPersonas(): any {
    this.personas = this.http
      .get(`/personas`, { sort: 'title' })
      .pipe(shareReplay(1), take(1));
  }

  apiGetPersonas(): Observable<any> {
    return this.http
      .get(`/personas`, { sort: 'title' })
  }

  getUserIdentifier(): Observable<any> {
    return this.http
      .get(`/profiles`)
      .pipe(map((profile) => profile.company.identifier));
  }

  async getAWSCredentials(): Promise<any> {
    return this.awsService.fetchAuthSession();
    // return this.awsService.getTempCredentials();
  }

  async uploadDocument(folder, file: File, progressCallback?: any) {
    this.updateAWSConfig();
    const s3 = new AWS.S3({
      apiVersion: environment.AWS_CONFIG.Storage.S3.apiVersion,
    });
    const Key = `documents/${folder}/${this.companyIdentifier}/${this.randomDigits(5)}_${file.name}`;
    const params = {
      Bucket: environment.AWS_CONFIG.Storage.S3.bucket,
      Body: file,
      Key,
      ContentType: file.type,
      ServerSideEncryption: "AES256",
    };

    // If progressCallback is a function
    if (typeof progressCallback == 'function') {
      return new Promise((resolve, reject) => {
        s3.putObject(params, (err, data) => {
          if (err) reject(err)
          else resolve({key: Key})
        }).on('httpUploadProgress', progressCallback);
      })
    }

    return new Promise((resolve, reject) => {
      s3.putObject(params, (err, data) => {
        if (err) reject(err)
        else resolve({key: Key})
      });
    });
  }

  _s3GetObject(key: string, options?: { isDownload?: boolean }): Observable<any> {
    this.updateAWSConfig();
    const s3 = new AWS.S3({
      apiVersion: environment.AWS_CONFIG.Storage.S3.apiVersion,
    });
    const _params = {
      Bucket: environment.AWS_CONFIG.Storage.S3.bucket,
      Key: key,
    };

    return new Observable((observer: Observer<any>) => {
      s3.getSignedUrl('getObject', _params, function(err, data) {
        if (err) observer.error(err)
        else observer.next(data)
      });
    });
  }
  processDocumentFromUrl(key: string, options?: { isDownload?: boolean }): Observable<any> {
    return this._s3GetObject(key, options);
  }

  uploadAssetsFile(file: File, userIdentifier, error, next): any {
    this.updateAWSConfig();
    const s3 = new AWS.S3({
      apiVersion: environment.AWS_CONFIG.Storage.S3.apiVersion,
    });
    const params = {
      Bucket: environment.AWS_CONFIG.Storage.S3.bucket,
      Body: file,
      Key: `assets/${userIdentifier}/${this.randomDigits(5)}-${file.name}`,
      ContentType: file.type,
      CacheControl: 'max-age=86400',
      ACL: 'public-read',
    };
    return s3
      .upload(params, (err, data) => {
        !err ? next(data) : error(err);
      });
  }

  getDrivers(): Observable<any> {
    return this.http
      .get(`/users`, { sort: 'name ASC', where: { status: 2 } });
  }

  getLocations(): Observable<any> {
    return this.http.get('/companies/locations');
  }

  getProductHazard(): Observable<any> {
    return this.http.get('/product-hazards?sort=name');
  }

  getCargoTypes(): Observable<any> {
    return this.http.get('/cargo-types?sort=name');
  }

  getCompanyIdentifier(): void {
    const userProfile = JSON.parse(localStorage.getItem('userProfile'));
    if (userProfile) {
      this.companyIdentifier = userProfile.company.identifier;
    } else {
      this.getUserIdentifier().subscribe((res) => {
        this.companyIdentifier = res;
      });
    }
  }

  checkCredentials(): Promise<any> {
    const currentCredential = JSON.parse(localStorage.getItem('credential'));
    if (currentCredential) {
      const now = new Date();
      const expirationTime = currentCredential.expiration * 1000;
      if (expirationTime > now.getTime()) {
        this.credentialOfUser = currentCredential;
        return;
      }
    }
    return this.getCredential();
  }

  getCredential(): Promise<any> {
    return this.getAWSCredentials().then((res) => {
      localStorage.setItem('credential', JSON.stringify(res));
      this.credentialOfUser = res;
    });
  }

  processFile(url, options: { isDownload?: boolean, index?: number } = { isDownload: false }): void {
    if (options?.isDownload) {
      this.downloadState[options.index] = true;
    }
    this.awsService.getUrlFile(url).then((res: any) => {
      let link = res?.url?.href;
      // if (options?.isDownload) {
      //   // this.downloadState[options.index] = false;
      //   // HelperService.downloadFile(res?.body, res?.fileName, res?.contentType);
      //   link = res?.url?.href
      // } else {
      //   console.log(res);
      //   const { url } = res;
      //   link = `${url?.origin}${url.pathname}`
      // }
      window.open(link);
    }).catch((err) => {
      this.downloadState[options.index] = false;
      throw new Error(err?.error);
    })

    this.processDocumentFromUrl(url, options).subscribe((res) => {
      if (res?.error) {
        this.downloadState[options.index] = false;
        throw new Error(res?.error);
      }

      if (options?.isDownload) {
        this.downloadState[options.index] = false;
        HelperService.downloadFile(res?.body, res?.fileName, res?.contentType);
      } else {
        window.open(res);
      }
    });
  }

  updateAWSConfig(): void {
    AWS.config.update({
      region: environment.AWS_CONFIG.Storage.S3.region,
      credentials: this.credentialOfUser,
    });
  }

  getUserProfile(): void {
    this.awsService.getUserInfo().subscribe((userInfo) => {
      localStorage.setItem('userProfile', JSON.stringify(userInfo));
    });
  }

  verifyDocument(key: string) {
    return this.http.post('/documents/verify', {s3_key: key});
  }

  verifyDocumentReport({s3_key, hash}: {s3_key: string, hash: string}) {
    return this.http.post('/documents/verify_and_report', {s3_key, hash});
  }

  public randomDigits(length: number = 5): string {
    const char =
      '1234567890abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
    let randomValue = '';
    for (let i = 0; i < length; i++) {
      const index = Math.floor(Math.random() * char.length);
      randomValue += char.substring(index, index + 1);
    }
    return randomValue;
  }

}
